import moment from "moment";
import { METODI_PAGAMENTO } from "./metodoPagamento";
export default {
  props: {
    contabile: Object,
  },
  data() {
    return {
      dialogElimina: false,
      loadingElimina: false,
      dialogConfermaSaldato: false,
      nuovoSaldato: null,
      saldato: this.contabile.saldato,
      loadingSaldato: false,
      alertError: false,
      erroreElimina: null,
      // Menu rapido di scelta del metodo di pagamento, aperto dalla spunta
      // quando si marca un debito come pagato (vedi toggle_saldato).
      metodoMenuOpen: false,
      metodiPagamento: METODI_PAGAMENTO,
    };
  },
  watch: {
    "contabile.saldato"(newVal) {
      // Riallinea la checkbox se il valore in store cambia dall'esterno
      // (socket CONTABILI_CHANGED, refresh, altra scheda/utente). Non tocca
      // lo stato mentre una nostra richiesta è in corso: ci pensa già
      // setSaldato() a riallineare al termine.
      if (!this.loadingSaldato) {
        this.saldato = newVal;
      }
    },
  },
  computed: {
    cliente() {
      if (!this.contabile || !this.contabile.cliente) return null;
      return this.$store.getters.get_cliente_by_iva(this.contabile.cliente);
    },
    dataFormatted() {
      if (!this.contabile || !this.contabile.ts) return "";
      return moment.unix(this.contabile.ts).format("dddd DD MMMM YYYY");
    },
    ordine() {
      return this.$store.getters.get_ordine_by_id(this.contabile.id_ordine);
    },
    // Ha un acconto registrato ma non è (ancora) interamente coperto:
    // stato "a metà strada" da segnalare con la spunta indeterminata.
    isPartial() {
      return (
        !this.saldato &&
        this.contabile &&
        this.contabile.importo_coperto !== undefined
      );
    },
    // Quanto manca a saldare del tutto questo debito in questo momento
    // (già al netto di eventuali acconti registrati).
    residuoAttuale() {
      if (!this.contabile) return 0;
      return this.contabile.residuo !== undefined
        ? this.contabile.residuo
        : Math.abs(this.contabile.importo || 0);
    },
    // Un debito già interamente allocato (nessun residuo aperto) ma non
    // saldato perché la copertura arriva da una Fattura Anticipata il cui
    // piano di pagamento non ha ancora realizzato nulla (0%, round 56-bis
    // — con la realizzazione proporzionale, appena viene pagata una
    // prima rata questo caso sparisce da solo: il residuo torna a
    // esistere genuinamente e passa al ramo normale sotto, con la
    // percentuale già pagata visibile lì). Niente da registrare qui,
    // quindi la spunta va disattivata invece di riproporre il menu di
    // pagamento.
    attesaFatturaBloccante() {
      return (
        !this.saldato &&
        this.contabile &&
        !!this.contabile.in_attesa_fattura &&
        (this.contabile.residuo === undefined || this.contabile.residuo <= 0)
      );
    },
    // Elenco DDT delle fatture anticipate che coprono (in tutto o in
    // parte, non ancora saldate) questo debito, per il tooltip.
    ddtAttesaFattura() {
      if (!this.contabile || !this.contabile.in_attesa_fattura) return "";
      return this.contabile.in_attesa_fattura.fatture
        .map((f) => f.ddt)
        .filter(Boolean)
        .join(", ");
    },
    // Percentuale già pagata del piano di rate della/e fattura/e che
    // copre/coprono questo debito (round 56-bis) — per il tooltip.
    percentualePagataAttesaFattura() {
      if (!this.contabile || !this.contabile.in_attesa_fattura) return "";
      return this.contabile.in_attesa_fattura.fatture
        .map((f) => (f.percentuale_pagata !== undefined ? f.percentuale_pagata + "%" : null))
        .filter(Boolean)
        .join(", ");
    },
    // "Residuo" ha un significato opposto a seconda del tipo di movimento
    // (round 65-bis): per un Debito è la quota ANCORA DA COPRIRE (nulla la
    // copre ancora — un "manca"), per un Credito/Fattura di Copertura è la
    // quota ANCORA DISPONIBILE per coprire futuri debiti (un "avanza").
    // Stessa colonna, stessa etichetta di testata, ma icona/colore/tooltip
    // distinti perché non vengano lette come la stessa cosa a colpo
    // d'occhio.
    residuoColorName() {
      if (!this.contabile || this.contabile.residuo === undefined) return "";
      if (this.contabile.residuo < 0) return "red"; // anomalia in entrambi i casi, mai atteso
      return this.contabile.tipo === 1 ? "orange" : "teal";
    },
    residuoIcon() {
      if (!this.contabile || this.contabile.residuo === undefined) return "";
      return this.contabile.tipo === 1 ? "mdi-cash-minus" : "mdi-cash-plus";
    },
    residuoTooltip() {
      if (!this.contabile || this.contabile.residuo === undefined) return "";
      if (this.contabile.tipo === 1) {
        return this.contabile.importo_coperto !== undefined
          ? `Acconto già ricevuto: € ${this.contabile.importo_coperto.toFixed(2)}`
          : "Ancora da coprire";
      }
      return "Credito ancora disponibile";
    },
    // Documenti che coprono (in tutto o in parte) questo debito — round
    // 65-sexies, colonna "Documento": a differenza di in_attesa_fattura
    // (solo la quota non ancora realizzata) resta presente anche a debito
    // saldato, per poter risalire sempre a quale Fattura di Copertura o
    // Pagamento Cliente lo ha coperto. Le due fonti hanno un'icona/colore
    // diversa (viola per una Fattura, stesso colore già usato ovunque per
    // il tipo=3; teal per un Pagamento Cliente, stesso colore scelto per
    // "credito disponibile" nel residuo — vedi residuoColorName sopra).
    documentiCollegatiFattura() {
      if (!this.contabile || !this.contabile.documenti_collegati) return [];
      return this.contabile.documenti_collegati.filter((d) => d.tipo === 3);
    },
    documentiCollegatiCredito() {
      if (!this.contabile || !this.contabile.documenti_collegati) return [];
      return this.contabile.documenti_collegati.filter((d) => d.tipo === 2);
    },
    tooltipDocumentiFattura() {
      return this.documentiCollegatiFattura
        .map((d) => {
          const rif = d.ddt ? `DDT ${d.ddt}` : "fattura";
          const stato = d.saldato ? "saldata" : `pagata al ${d.percentuale_pagata}%`;
          return `${rif} (${stato})`;
        })
        .join(", ");
    },
    tooltipDocumentiCredito() {
      // Un Pagamento Cliente quasi mai ha un DDT (non è un documento di
      // trasporto): a differenza della Fattura sopra, qui il fallback
      // "pagamento cliente" non diceva nulla di nuovo rispetto
      // all'intestazione del tooltip che lo introduce già — la sua data
      // identifica quale pagamento, in un elenco che può averne più di
      // uno (round 65-octies).
      return this.documentiCollegatiCredito
        .map((d) => (d.ddt ? `DDT ${d.ddt}` : `del ${this.formatData(d.ts)}`))
        .join(", ");
    },
  },
  methods: {
    toggle_saldato(instant) {
      if (this.contabile && this.contabile.fake) return; // ignora placeholder
      if (this.contabile && this.contabile.tipo === 2) return; // nota di credito: sempre saldata, non modificabile
      if (this.loadingSaldato) return; // richiesta già in corso, evita doppio invio

      if (this.contabile && this.contabile.tipo === 1) {
        // Debito "normale": la spunta ora passa sempre da un'allocazione
        // (per tracciare data/metodo/importo, non solo un flag), quindi si
        // comporta diversamente a seconda della direzione:
        if (this.saldato) {
          // Segna come "non pagato": rimuove le coperture già registrate,
          // un'azione più delicata di un semplice flip → chiede conferma.
          this.dialogConfermaSaldato = true;
        } else {
          // Non ancora coperto (o solo in parte): la scelta del metodo nel
          // menu vale già da conferma, nessun altro passaggio.
          this.metodoMenuOpen = true;
        }
        return;
      }

      // Altri casi (es. Fattura Anticipata già saldata, da smarcare — non
      // ancora saldata invece non passa più da qui: round 56-quater ha
      // tolto la scorciatoia diretta, va sempre creata prima una rata,
      // anche una sola, dal dialog "Erosione credito").
      if (instant) {
        this.saldato = !this.saldato;
        this.setSaldato(this.saldato);
      } else {
        this.dialogConfermaSaldato = true;
      }
    },
    // Usata solo per un Debito (tipo=1): per una Fattura Anticipata non
    // esiste più una scorciatoia di pagamento rapido dalla lista (round
    // 56-quater) — va sempre creata prima una rata, anche una sola, dal
    // dialog "Erosione credito".
    registraPagamentoRapido(metodo) {
      if (this.contabile && this.contabile.fake) return; // ignora placeholder
      this.metodoMenuOpen = false;
      this.loadingSaldato = true;
      this.$store
        .dispatch("crea_allocazione", {
          id_debito: this.contabile.id,
          importo: this.residuoAttuale,
          id_credito: null,
          metodo_pagamento: metodo,
        })
        .then((data) => {
          if (data && data.debito) {
            this.saldato = data.debito.saldato;
          }
          // Aggiorna anche residuo/acconto visibili sulla riga: come per
          // le altre azioni di allocazione, un refresh reale è la via più
          // affidabile per farli comparire subito.
          this.$emit("new_insert");
        })
        .catch(() => {
          this.alertError = true;
        })
        .finally(() => {
          this.loadingSaldato = false;
        });
    },
    async annullaTutteLeCoperture() {
      if (this.contabile && this.contabile.fake) return; // ignora placeholder
      this.loadingSaldato = true;
      try {
        await this.$store.dispatch(
          "elimina_tutte_allocazioni_debito",
          this.contabile.id
        );
        this.saldato = false;
        this.$emit("new_insert");
      } catch (err) {
        this.alertError = true;
      } finally {
        this.loadingSaldato = false;
      }
    },
    async eliminaContabile() {
      if (this.contabile && this.contabile.fake) return; // ignora placeholder
      this.loadingElimina = true;
      this.erroreElimina = null;
      try {
        await this.$store.dispatch("delete_contabile", this.contabile.id);
        this.dialogElimina = false;
        this.$emit("new_insert");
      } catch (err) {
        // Es. eliminazione rifiutata perché un credito ha ancora debiti
        // che lo erodono: il dialog resta aperto e mostra il motivo,
        // invece di lasciare lo spinner bloccato in silenzio.
        this.erroreElimina =
          err.response?.data?.message || "Errore durante l'eliminazione";
      } finally {
        this.loadingElimina = false;
      }
    },
    async confermaSaldato() {
      if (this.contabile && this.contabile.fake) return; // ignora placeholder
      if (this.loadingSaldato) return; // richiesta già in corso, evita doppio invio
      this.dialogConfermaSaldato = false;
      if (this.contabile && this.contabile.tipo === 1 && this.saldato) {
        // Debito coperto da allocazioni: la conferma rimuove tutte le
        // coperture registrate, non un semplice flip del flag.
        this.annullaTutteLeCoperture();
      } else {
        this.saldato = !this.saldato;
        this.setSaldato(this.saldato);
      }
    },
    annullaSaldato() {
      this.dialogConfermaSaldato = false;
    },
    setSaldato(val) {
      if (this.contabile && this.contabile.fake) return; // ignora placeholder
      this.loadingSaldato = true;
      this.$store
        .dispatch("toggle_contabile_saldato", {
          id: this.contabile.id,
          stato: val,
        })
        .then((response) => {
          // Riallinea la checkbox al valore realmente salvato dal server,
          // non a quello ipotizzato in ottimistico.
          if (response && response.data) {
            this.saldato = response.data.saldato;
          }
          if (response && response.data && response.data.debiti_aggiornati) {
            // Un credito/fattura anticipata con debiti vincolati: il
            // saldato è appena stato propagato a cascata su di loro.
            // Come per il residuo, forziamo un refresh reale della lista
            // invece di fidarci della sola patch locale allo store, per
            // essere sicuri che le loro righe si aggiornino.
            this.$emit("new_insert");
          }
        })
        .catch(() => {
          // Copre sia l'errore esplicito del server sia il timeout: in
          // entrambi i casi non possiamo assumere che il salvataggio sia
          // andato a buon fine, quindi torniamo allo stato precedente.
          this.alertError = true;
          this.saldato = !val;
        })
        .finally(() => {
          this.loadingSaldato = false;
        });
    },
    formatData(ts) {
      if (ts) {
        return moment.unix(ts).format("DD-MM-YYYY");
      }
      return "";
    },
    openDettaglioOrdine() {
      if (!this.ordine) {
        this.$store
          .dispatch("fetch_ordine_by_id", this.contabile.id_ordine)
          .then(() => {
            this.$refs.ordineDialog.open();
          });
      } else {
        this.$refs.ordineDialog.open();
      }
    },
    async updateContabile() {
      // Notifica il parent di aggiornare la lista
      this.$emit("new_insert");
      // Attendi il prossimo tick così il parent finisce refreshList e il ref è di nuovo montato
      await this.$nextTick();
      if (
        this.$refs.contabileOrdineRef &&
        this.$refs.contabileOrdineRef.loadOrdine
      ) {
        this.$refs.contabileOrdineRef.loadOrdine();
      }
    },
  },
};
